import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Zap, Flame, Coins, Star, ChevronRight, Sparkles, CheckCircle2, Lock, Play, TrendingUp, Swords, Rocket, Target, Shield, Gift, MessageSquare, Award,
} from "lucide-react";
import ProgressRing from "../components/ProgressRing";
import BadgeIcon from "../components/BadgeIcon";
import Btn from "../components/Btn";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => { setData(data); setUser(data.user); }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  if (!data) return <SkeletonDash />;
  const u = data.user;
  const levelPct = u.exp_needed ? u.exp_into_level / u.exp_needed : 0;

  const skillEntries = Object.entries(data.skills || {});
  const badgeDefs = data.badge_defs || {};

  return (
    <div className="space-y-6">
      {/* success testimonial CTA */}
      <Link to="/testimoni-saya" data-testid="dashboard-testimoni-cta"
        className="block card-chunky p-4 hover:-translate-y-0.5 transition-transform bg-gradient-to-r from-su-success/10 to-su-primary/10">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-su-success/20 grid place-items-center shrink-0"><Award className="w-6 h-6 text-su-success" /></div>
          <div className="flex-1 min-w-0">
            <p className="font-heading font-black">Sudah lolos lewat SkorUp? 🎉</p>
            <p className="text-sm text-su-muted font-medium truncate">Bagikan testimoni keberhasilanmu & tampil di Beranda.</p>
          </div>
          <ChevronRight className="w-5 h-5 text-su-muted shrink-0" />
        </div>
      </Link>

      {/* greeting */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-su-muted font-heading font-extrabold text-sm">Halo lagi, pejuang 👋</p>
          <h1 className="font-heading font-black text-2xl sm:text-3xl">{u.name || "Player"}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/arena"><Btn data-testid="dash-arena" variant="danger"><Swords className="w-5 h-5" /> Arena 1v1</Btn></Link>
          <Link to="/mega"><Btn data-testid="dash-mega" variant="secondary"><Rocket className="w-5 h-5" /> Tryout Akbar</Btn></Link>
          <Link to="/tryouts"><Btn data-testid="dash-start-tryout"><Play className="w-5 h-5 fill-current" /> Mulai Tryout</Btn></Link>
        </div>
      </div>

      {/* top stat row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* level ring */}
        <div className="card-chunky p-5 col-span-2 lg:col-span-1 flex items-center gap-4">
          <ProgressRing progress={levelPct} size={92} stroke={10}>
            <div className="text-center">
              <div className="font-mono font-bold text-2xl text-su-primary leading-none" data-testid="dash-level">{u.level}</div>
              <div className="text-[9px] font-heading font-black uppercase text-su-muted">Level</div>
            </div>
          </ProgressRing>
          <div>
            <p className="text-xs font-heading font-black uppercase text-su-muted">EXP</p>
            <p className="font-heading font-black text-xl" data-testid="dash-exp">{u.exp.toLocaleString()}</p>
            <p className="text-xs text-su-muted font-bold">{u.exp_into_level}/{u.exp_needed} ke Lv {u.level + 1}</p>
          </div>
        </div>
        <StatCard icon={Zap} color="var(--su-energy)" label="Energi" value={`${u.energy}/${u.max_energy}`} testid="dash-energy" />
        <StatCard icon={Flame} color="#f97316" label="Streak" value={`${u.streak} hari`} testid="dash-streak" />
        <StatCard icon={Coins} color="var(--su-secondaryShadow)" label="Token" value={u.tokens} testid="dash-tokens" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* left: skill tree + recent */}
        <div className="lg:col-span-2 space-y-6">
          {/* skill tree */}
          <section className="card-chunky p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading font-black text-xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-su-primary" /> Skill Tree</h2>
              <Link to="/drills" className="text-sm font-heading font-extrabold text-su-primary flex items-center gap-1">Latih <ChevronRight className="w-4 h-4" /></Link>
            </div>
            {skillEntries.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-2xl bg-su-track grid place-items-center mx-auto mb-3"><Lock className="w-7 h-7 text-su-muted" /></div>
                <p className="font-heading font-extrabold text-su-muted">Belum ada skill terbuka</p>
                <p className="text-sm text-su-muted font-medium">Selesaikan tryout pertamamu untuk membuka skill tree.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {skillEntries.map(([name, s]) => {
                  const acc = s.accuracy || 0;
                  const done = acc >= 70;
                  return (
                    <div key={name} className={`rounded-2xl border-2 p-4 flex items-center gap-3 ${done ? "border-su-success bg-su-success/5" : "border-su-border"}`} data-testid={`skill-${name}`}>
                      <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${done ? "bg-su-success text-white" : "bg-su-primary/15 text-su-primary"}`}>
                        {done ? <CheckCircle2 className="w-6 h-6" /> : <TrendingUp className="w-5 h-5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-heading font-extrabold text-sm truncate">{name}</p>
                        <div className="h-2 rounded-full bg-su-track mt-1.5 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${acc}%`, background: done ? "var(--su-success)" : "var(--su-primary)" }} />
                        </div>
                        <p className="text-xs text-su-muted font-bold mt-1">Akurasi {acc}%</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* recent attempts */}
          <section className="card-chunky p-6">
            <h2 className="font-heading font-black text-xl mb-4">Tryout Terakhir</h2>
            {data.recent_attempts.length === 0 ? (
              <p className="text-su-muted font-medium text-sm py-4 text-center">Belum ada riwayat. Ayo mulai tryout pertamamu!</p>
            ) : (
              <div className="space-y-3">
                {data.recent_attempts.map((a) => (
                  <Link to={`/result/${a.attempt_id}`} key={a.attempt_id} data-testid={`recent-${a.attempt_id}`}
                    className="flex items-center gap-3 rounded-2xl border-2 border-su-border p-3 hover:border-su-primary transition-colors">
                    <div className="w-11 h-11 rounded-xl bg-su-primary/15 text-su-primary grid place-items-center font-heading font-black">{Math.round(a.accuracy)}%</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-extrabold truncate">{a.title}</p>
                      <p className="text-xs text-su-muted font-bold">Skor {a.score} · {new Date(a.submitted_at).toLocaleDateString("id-ID")}</p>
                    </div>
                    {a.passed === true && <span className="text-xs font-heading font-black text-su-success">LULUS</span>}
                    {a.passed === false && <span className="text-xs font-heading font-black text-su-danger">BELUM</span>}
                    <ChevronRight className="w-5 h-5 text-su-muted" />
                  </Link>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* right: badges + stats */}
        <div className="space-y-6">
          <section className="card-chunky p-6">
            <h2 className="font-heading font-black text-xl mb-4 flex items-center gap-2"><Star className="w-5 h-5 text-su-secondary fill-current" /> Koleksi Badge</h2>
            <div className="grid grid-cols-3 gap-y-4 gap-x-1 justify-items-center">
              {Object.entries(badgeDefs).map(([id, b]) => (
                <BadgeIcon key={id} badge={b} unlocked={u.badges.includes(id)} size={56} />
              ))}
            </div>
          </section>

          <section className="card-chunky p-6">
            <h2 className="font-heading font-black text-xl mb-4">Statistik</h2>
            <div className="space-y-3 font-heading font-extrabold">
              <Row label="Tryout selesai" value={u.stats.tests_taken} />
              <Row label="Soal dijawab" value={u.stats.questions_answered} />
              <Row label="Jawaban benar" value={u.stats.correct} />
              <Row label="ELO Arena" value={u.elo} />
            </div>
          </section>

          <section className="card-chunky p-6">
            <h2 className="font-heading font-black text-xl mb-4">Jelajahi</h2>
            <div className="grid grid-cols-1 gap-2">
              <ShortcutLink to="/missions" icon={Sparkles} color="var(--su-secondaryShadow)" title="Misi Harian" desc="Klaim bonus tiap hari" testid="shortcut-missions" />
              <ShortcutLink to="/drills" icon={Target} color="var(--su-energy)" title="Drill Adaptif" desc="Latih titik lemahmu" testid="shortcut-drills" />
              <ShortcutLink to="/guilds" icon={Shield} color="var(--su-primary)" title="Study Guild" desc="Belajar bareng clan" testid="shortcut-guilds" />
              <ShortcutLink to="/referral" icon={Gift} color="var(--su-success)" title="Ajak Teman" desc="Bonus token & energi" testid="shortcut-referral" />
              <ShortcutLink to="/feedback" icon={MessageSquare} color="var(--su-secondary)" title="Saran & Lapor Bug" desc="Kirim masukan ke admin" testid="shortcut-feedback" />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value, testid }) {
  return (
    <motion.div whileHover={{ y: -3 }} className="card-chunky p-4" data-testid={testid}>
      <div className="w-10 h-10 rounded-xl grid place-items-center mb-2" style={{ background: `${color}22` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <p className="text-xs font-heading font-black uppercase text-su-muted">{label}</p>
      <p className="font-heading font-black text-xl">{value}</p>
    </motion.div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-su-muted text-sm">{label}</span>
      <span className="font-mono">{value?.toLocaleString?.() || value}</span>
    </div>
  );
}

function ShortcutLink({ to, icon: Icon, color, title, desc, testid }) {
  return (
    <Link to={to} data-testid={testid} className="flex items-center gap-3 rounded-2xl border-2 border-su-border p-3 hover:border-su-primary transition-colors">
      <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: `${color}22` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-heading font-extrabold text-sm">{title}</p>
        <p className="text-xs text-su-muted font-bold">{desc}</p>
      </div>
      <ChevronRight className="w-5 h-5 text-su-muted" />
    </Link>
  );
}

function SkeletonDash() {
  return (
    <div className="space-y-4">
      <div className="h-10 w-48 rounded-2xl bg-su-track animate-pulse" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <div key={i} className="h-28 rounded-3xl bg-su-track animate-pulse" />)}
      </div>
      <div className="h-64 rounded-3xl bg-su-track animate-pulse" />
    </div>
  );
}
